import type { QueryClient } from '@tanstack/react-query';
import { toast } from '@tempo-io/sui-sonner';
import { createExpiringIdRegistry } from '@/common/utils/expiringIdRegistry';
import { createKeyedStore } from '@/common/utils/keyedStore';
import { SELF_EDIT_ATTRIBUTION_TTL_MS } from './itemsTreeSetConstants';
import type { TreeSetDiff } from './treeSetDiff';

/** A self-edit's auto-promoted diff that dropped at least one row from the response entirely. */
export type RemovedNotice = {
  diff: TreeSetDiff;
};

// clearOnUnsubscribe stays false so a remount can't discard an unconsumed notice. Unbounded
// search-term keys are swept instead by itemsTreeSetConsistency.ts's startQueryRemovalCleanup.
const store = createKeyedStore<RemovedNotice>({ clearOnUnsubscribe: false });

export const getRemovedNotice = (queryKeyString: string): RemovedNotice | undefined =>
  store.get(queryKeyString);

export const subscribeRemovedNotice = (
  queryKeyString: string,
  listener: () => void,
): (() => void) => store.subscribe(queryKeyString, listener);

/** Overwrites rather than queues — a later reconciliation supersedes whatever was published before. */
export const publishRemovedNotice = (queryKeyString: string, diff: TreeSetDiff): void =>
  store.set(queryKeyString, { diff });

export const clearRemovedNotice = (queryKeyString: string): void => store.delete(queryKeyString);

/**
 * Ids that must never surface a "filtered out" toast — the explicit-delete bypass. Per-client, not
 * per-key: every mounted table on that client should skip it. The TTL matches
 * `itemsTreeSetConsistency.ts`'s self-edit attribution window, since `publishRemovedNotice` can
 * never fire for an id past that window anyway.
 */
const excludedIdsRegistry = createExpiringIdRegistry(SELF_EDIT_ATTRIBUTION_TTL_MS);

export const excludeFromRemovedNotice = (
  queryClient: QueryClient,
  itemIds: readonly number[],
): void => excludedIdsRegistry.record(queryClient, itemIds);

/** Reverses `excludeFromRemovedNotice` — for a delete that turned out to fail. */
export const forgetFromRemovedNotice = (
  queryClient: QueryClient,
  itemIds: readonly number[],
): void => excludedIdsRegistry.forget(queryClient, itemIds);

export const isExcludedFromRemovedNotice = (queryClient: QueryClient, id: number): boolean =>
  excludedIdsRegistry.has(queryClient, id);

type RetractionListener = (itemId: number) => void;
const retractionListenersByClient = new WeakMap<QueryClient, Set<RetractionListener>>();

const retractionListenersFor = (queryClient: QueryClient): Set<RetractionListener> => {
  let listeners = retractionListenersByClient.get(queryClient);
  if (!listeners) {
    listeners = new Set();
    retractionListenersByClient.set(queryClient, listeners);
  }
  return listeners;
};

// Sonner toasts outlive their component; a Set since the same item can have one per mounted view.
const activeToastsByClient = new WeakMap<QueryClient, Map<number, Set<string | number>>>();

const activeToastsFor = (queryClient: QueryClient): Map<number, Set<string | number>> => {
  let toasts = activeToastsByClient.get(queryClient);
  if (!toasts) {
    toasts = new Map();
    activeToastsByClient.set(queryClient, toasts);
  }
  return toasts;
};

export const setActiveRemovedNoticeToast = (
  queryClient: QueryClient,
  itemId: number,
  toastId: string | number,
): void => {
  const toasts = activeToastsFor(queryClient);
  let ids = toasts.get(itemId);
  if (!ids) {
    ids = new Set();
    toasts.set(itemId, ids);
  }
  ids.add(toastId);
};

/** Removes just this one caller's own registration — another view's toast for the same item stays. */
export const clearActiveRemovedNoticeToast = (
  queryClient: QueryClient,
  itemId: number,
  toastId: string | number,
): void => {
  const ids = activeToastsFor(queryClient).get(itemId);
  if (!ids) return;
  ids.delete(toastId);
  if (ids.size === 0) activeToastsFor(queryClient).delete(itemId);
};

export const hasActiveRemovedNoticeToast = (
  queryClient: QueryClient,
  itemId: number,
  toastId: string | number,
): boolean => activeToastsByClient.get(queryClient)?.get(itemId)?.has(toastId) ?? false;

/** Full teardown for this client — a real table never needs it, but a test sharing one client does. */
export const clearAllActiveRemovedNoticeToasts = (queryClient: QueryClient): void => {
  activeToastsFor(queryClient).clear();
};

/** Dismisses every shown toast for this item, and scrubs it from any unconsumed notice too. */
export const retractRemovedNoticeToast = (queryClient: QueryClient, itemId: number): void => {
  for (const listener of retractionListenersFor(queryClient)) listener(itemId);
  const toasts = activeToastsFor(queryClient);
  const ids = toasts.get(itemId);
  if (ids) {
    for (const toastId of ids) toast.dismiss(toastId);
    toasts.delete(itemId);
  }
  for (const [key, notice] of store.entries()) {
    if (!notice.diff.removedIds.includes(itemId)) continue;
    const removedIds = notice.diff.removedIds.filter((id) => id !== itemId);
    if (removedIds.length === 0) store.delete(key);
    else store.set(key, { diff: { ...notice.diff, removedIds } });
  }
};

export const subscribeRemovedNoticeRetraction = (
  queryClient: QueryClient,
  listener: RetractionListener,
): (() => void) => {
  const listeners = retractionListenersFor(queryClient);
  listeners.add(listener);
  return () => listeners.delete(listener);
};
